import React, { forwardRef, useMemo, useState, useEffect } from 'react';
import { useImageKitDelivery } from '../contexts/ImageKitConfigContext';
import { buildVideoDeliveryUrl, canUseMediaDeliveryLayer } from '../utils/mediaImageUtils';

/**
 * S3-backed video via ImageKit (when linked) or API stream proxy — not raw S3 URLs.
 * Public paths like /reels/foo.mp4 are passed through unchanged.
 */
const ResponsiveVideo = forwardRef(function ResponsiveVideo(
  {
    src,
    deliveryWidth = 720,
    poster,
    className,
    onError,
    onDeliveredSrcReady,
    ...rest
  },
  ref
) {
  const {
    endpoint,
    deliveryMode,
    deliveryReady,
    mediaVideoDeliveryBase
  } = useImageKitDelivery();
  const [forceOrigin, setForceOrigin] = useState(false);

  const deliveredSrc = useMemo(() => {
    if (!src || forceOrigin || !canUseMediaDeliveryLayer(src)) return src;
    const mode = deliveryReady && deliveryMode === 'imagekit' ? 'imagekit' : 'proxy';
    return buildVideoDeliveryUrl(src, {
      width: deliveryWidth,
      endpoint,
      deliveryMode: mode,
      mediaVideoDeliveryBase
    });
  }, [
    src,
    forceOrigin,
    deliveryWidth,
    endpoint,
    deliveryMode,
    deliveryReady,
    mediaVideoDeliveryBase
  ]);

  useEffect(() => {
    onDeliveredSrcReady?.(deliveredSrc);
  }, [deliveredSrc, onDeliveredSrcReady]);

  const handleError = (event) => {
    if (deliveredSrc !== src && !forceOrigin) {
      setForceOrigin(true);
    }
    onError?.(event);
  };

  const mergeRef = (node) => {
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
    if (node && rest.playsInline) {
      node.setAttribute('playsinline', '');
      node.setAttribute('webkit-playsinline', 'true');
    }
  };

  return (
    <video
      {...rest}
      ref={mergeRef}
      src={deliveredSrc}
      poster={poster}
      className={className}
      onError={handleError}
    />
  );
});

export default ResponsiveVideo;
